import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import JsonP from 'jsonp';
import qs from 'qs';
import { message } from 'antd';
import { judgeIEVersion, randomNumber } from './index';
import { cookie } from '@gaodun.com/gtools';
import { loginTimeout } from '@utils';

interface AxiosOptions {
  baseURL: string,
  timeout?: number,
  headers?: object,
  requestInterceptors?: (request: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>,
  requestInterceptorsError?: (error: any) => any,
  responseInterceptors?: (response: AxiosResponse) => Promise<AxiosResponse> | AxiosResponse,
  responseInterceptorsError?: (error: any) => any,
}

interface Axios {
  http: AxiosInstance,
  baseURL: string,
  timeout: number,
  headers: object,
  requestInterceptors: (request: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>,
  responseInterceptors: (response: AxiosResponse) => Promise<AxiosResponse> | AxiosResponse,
  requestInterceptorsError: (error: any) => any,
  responseInterceptorsError: (error: any) => any,
  env: string,
}

class Axios {
    constructor(options: AxiosOptions) {
        this.baseURL = options.baseURL || '';
        this.timeout = options.timeout || 60000;
        this.headers = options.headers ? { 'Content-Type': 'application/json', ...options.headers } : { 'Content-Type': 'application/json' };
        this.requestInterceptors = options.requestInterceptors || Axios.requestInterceptorsDefault;
        this.responseInterceptors = options.responseInterceptors || Axios.responseInterceptorsDefault;
        this.requestInterceptorsError = options.requestInterceptorsError || Axios.requestInterceptorsErrorDefault;
        this.responseInterceptorsError = options.responseInterceptorsError || Axios.responseInterceptorsError;
        this.init();
    }
    init() {
  	this.http = axios.create({
  		baseURL: this.baseURL,
  		headers: this.headers,
  		timeout: this.timeout,
  		transformRequest(data) {
  			let strData = qs.stringify(data);
  			return strData;
  		},
  	});
  	this.setInterceptors();
    }
    private setInterceptors() {
  	this.http.interceptors.request.use(this.requestInterceptors, this.requestInterceptorsError);
  	this.http.interceptors.response.use(this.responseInterceptors, this.responseInterceptorsError);
    }
    private static async requestInterceptorsDefault(config: any) {
	  const isVersion: number | string = judgeIEVersion();
  	if (typeof isVersion === 'number' && isVersion > 0 && isVersion <= 11) {
  		config.params = {
  			ie: Date.now(),
  			...config.params
  		};
  	}
  	const token: any = cookie.getCookie('accessAdmintoken');
  	config.headers = {
  		...config.headers,
  		...(token ? {
  			'Authentication': `Basic ${token}`
  		} : {})
  	};
  	return config;
    }
    private static responseInterceptorsDefault(response: any) {
  	let status: any = +response.data.status;

  	if (response.config.url.indexOf('/qa/user/message/v2/new') > -1){
  		return response;
  	}
	
  	if ((status > 553649000 && status < 563649999) || (status === 10160001)) {
  		loginTimeout();
  		return {
  			status: 'need-login',
  			message: '未登录'
  		};
  	}

  	// catch interception
  	if (status === 11053008) {
  		return 	message.error(response.data.message);
  	}

  	// 无权限提示
  	if (status === 11053601){
  		message.error(response.data.message);
  		return false;
  	}

  	if (status !== 0) {
  		status = response.data.status + '';//TODO zhushi
  		const fileStream = response.headers['content-type'];
  		if (status.length === 8 && +status.substr(4, 1) > 2) {
  			return response;
  		}
  		if (
  			(response.config.url.indexOf('/qasys/qa/general') > -1) ||
        (response.config.url.indexOf('.cn-hangzhou.log.aliyuncs.com/logstores/') > -1) ||
        (response.config.url.indexOf('/newoss') > -1) ||
        (response.config.url.indexOf('/vigo') > -1) ||
        (response.config.url.indexOf('/qa/user/message/new') > -1)||
        (response.config.url.indexOf('/glive2-vod/api/v1/check/') > -1)||
		(response.config.url.indexOf('/booking/process-export') > -1)
  		) {
  			return response;
  		}
  		if (fileStream.indexOf('application/vnd.ms-excel') > -1) {
  			return response;
  		}
  		message.error(String(response.data.message || response.data.info || '请求错误'));
  	}
  	return response;
    }
    private static requestInterceptorsErrorDefault(error: any) {
  	return Promise.resolve({
  		data: {
  			status: 1,
  			message: '错误请求'
  		}
  	});
    }
    //TODO
    private static responseInterceptorsError(error: any) {
  	if (axios.isCancel(error)) {
  		return Promise.resolve({
  			data: {
  				status: 1,
  				message: '取消请求',
  				isCancel: true
  			}
  		});
  	}
  	return Promise.resolve({
  		data: {
  			status: 1,
  			message: '错误请求'
  		}
  	});
    }

    //jsonp请求封装
    jsonp(options: any) {
  	return new Promise((resolve, reject) => {
  		JsonP(options.url, {
  			param: 'callback'
  		}, function (err, res) {
  			if (res.status === 'success') {
  				resolve(res);
  			} else {
  				reject(err);
  			}
  		});
  	});
    }

    request(options: any) {
  	return this.http.request(options);
    }

    get(url: string, params = {}, options = {}) {
  	return this.request({
  		method: 'get',
  		url,
  		params: { // 这里让get请求中的body中数据拼接到url后面
  			...params
  		},
  		...options
  	});
    }

    delete(url: string, options = {}) {
  	return this.request({
  		method: 'delete',
  		url,
  		params: {
  			...options
  		}
  	});
    }

    post(url: string, data: any = {}, options = {}) {
  	return this.request({
  		method: 'post',
  		url,
  		data,
  		transformRequest: [($data: any) => {
  			return JSON.stringify($data);
  		}],
  		...options
  	});
    }

    formPost(url: string, data: any = {}, options = {}) {
  	return this.request({
  		method: 'post',
  		url,
  		data,
  		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  		...options
  	});
    }

    put(url: string, data?: any, options = {}) {
  	return this.request({
  		method: 'put',
  		url,
  		data,
  		transformRequest: [($data: any) => {
  			return JSON.stringify($data);
  		}],
  		...options
  	});
    }

    formPut(url: string, data: any, options = {}) {
  	return this.request({
  		method: 'put',
  		url,
  		data,
  		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  		...options
  	});
    }
}
export default Axios;
